import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { Sentry } from './lib/sentry.ts';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
import { AuthProvider } from './context/AuthContext';
import { DataProvider } from './context/DataContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import Login from './pages/Login';
import { ReportPage } from './pages/Report';
import EstimatedRevenuePerManager from './pages/EstimatedRevenuePerManager';
import GroupSeriesReport from './pages/GroupSeriesReport';
import MaterializationPerClient from './pages/MaterializationPerClient';
import MaterializationPerClientHotel from './pages/MaterializationPerClientHotel';
import ClientHotelMaterialization from './pages/ClientHotelMaterialization';
import DestinationHotelMaterialization from './pages/DestinationHotelMaterialization';
import AverageRatesPerCity from './pages/AverageRatesPerCity';
import GroupsPerMonth from './pages/GroupsPerMonth';
import Dashboard from './pages/Dashboard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LostBusiness from './pages/LostBusiness';
import LeadershipTeamSalesUpdate from './pages/LeadershipTeamSalesUpdate';
import MaterializationPerSegment from './pages/MaterializationPerSegment';
import CreatedConfirmedRevenue from './pages/CreatedConfirmedRevenue';
import MeetingPreparation from './pages/MeetingPreparation';
import MaterializationPerMarketClient from './pages/MaterializationPerMarketClient';
import HotelsPerformance from './pages/HotelsPerformance';
import { AdminRoute } from './components/AdminRoute';
import McpAccess from './pages/McpAccess';
import { RevenueRoute } from './components/RevenueRoute';
import CampaignDashboard from './pages/revenue-collection/CampaignDashboard';
import CampaignSetup from './pages/revenue-collection/CampaignSetup';
import CampaignDetail from './pages/revenue-collection/CampaignDetail';
import TemplateManagement from './pages/revenue-collection/TemplateManagement';
import SalesManagerNotifications from './pages/revenue-collection/SalesManagerNotifications';
import HotelCommissions from './pages/revenue-collection/HotelCommissions';
import HotelContacts from './pages/revenue-collection/HotelContacts';
import HotelierForm from './pages/revenue-collection/HotelierForm';
import ThankYou from './pages/revenue-collection/ThankYou';

/** Keep old email / bookmark links working after the hotel-report path rename. */
function LegacyHotelierFormRedirect() {
  const { token } = useParams();
  return <Navigate to={`/hotel-report/form/${token ?? ''}`} replace />;
}

function LegacyThankYouRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/hotel-report/thank-you${search}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <Router>
          <SentryRoutes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/data" element={<Navigate to="/dashboard" replace />} />
          <Route path="/report" element={<ProtectedRoute><ReportPage /></ProtectedRoute>} />
          <Route
            path="/report/estimated-revenue" 
            element={<ProtectedRoute><EstimatedRevenuePerManager /></ProtectedRoute>} 
          />
          <Route
            path="/report/group-series" 
            element={<ProtectedRoute><GroupSeriesReport /></ProtectedRoute>} 
          />
          <Route
            path="/report/materialization-per-client"
            element={<ProtectedRoute><MaterializationPerClient /></ProtectedRoute>}
          />
          <Route
            path="/report/materialization-per-client-hotel"
            element={<ProtectedRoute><MaterializationPerClientHotel /></ProtectedRoute>}
          />
          <Route
            path="/report/client-hotel-materialization"
            element={<ProtectedRoute><ClientHotelMaterialization /></ProtectedRoute>}
          />
          <Route
            path="/report/destination-hotel-materialization"
            element={<ProtectedRoute><DestinationHotelMaterialization /></ProtectedRoute>}
          />
          <Route
            path="/report/hotels-performance"
            element={<ProtectedRoute><HotelsPerformance /></ProtectedRoute>}
          />
          <Route
            path="/report/materialization-per-segment"
            element={<ProtectedRoute><MaterializationPerSegment /></ProtectedRoute>}
          />
          <Route
            path="/report/average-rates-per-city"
            element={<ProtectedRoute><AverageRatesPerCity /></ProtectedRoute>}
          />
          <Route
            path="/report/groups-per-month"
            element={<ProtectedRoute><GroupsPerMonth /></ProtectedRoute>}
          />
          <Route
            path="/report/lost-business"
            element={<ProtectedRoute><LostBusiness /></ProtectedRoute>}
          />
          <Route
            path="/report/leadership-sales-update"
            element={<ProtectedRoute><LeadershipTeamSalesUpdate /></ProtectedRoute>}
          />
          <Route
            path="/report/created-confirmed-revenue"
            element={<ProtectedRoute><CreatedConfirmedRevenue /></ProtectedRoute>}
          />
          <Route
            path="/report/meeting-preparation"
            element={<ProtectedRoute><MeetingPreparation /></ProtectedRoute>}
          />
          <Route
            path="/report/materialization-per-market-client"
            element={<ProtectedRoute><MaterializationPerMarketClient /></ProtectedRoute>}
          />
          <Route
            path="/dashboard"
            element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
          />
          <Route
            path="/revenue-collection"
            element={<RevenueRoute><CampaignDashboard /></RevenueRoute>}
          />
          <Route
            path="/admin/mcp-access"
            element={<AdminRoute><McpAccess /></AdminRoute>}
          />
          <Route
            path="/revenue-collection/campaigns/new"
            element={<AdminRoute><CampaignSetup /></AdminRoute>}
          />
          <Route
            path="/revenue-collection/campaigns/:id/edit"
            element={<AdminRoute><CampaignSetup /></AdminRoute>}
          />
          <Route
            path="/revenue-collection/campaigns/:id"
            element={<RevenueRoute><CampaignDetail /></RevenueRoute>}
          />
          <Route
            path="/revenue-collection/commissions"
            element={<RevenueRoute><HotelCommissions /></RevenueRoute>}
          />
          <Route
            path="/revenue-collection/hotel-contacts"
            element={<RevenueRoute><HotelContacts /></RevenueRoute>}
          />
          <Route
            path="/revenue-collection/notifications"
            element={<RevenueRoute><SalesManagerNotifications /></RevenueRoute>}
          />
          <Route
            path="/revenue-collection/templates"
            element={<RevenueRoute><TemplateManagement /></RevenueRoute>}
          />
          <Route
            path="/hotel-report/form/:token"
            element={<HotelierForm />}
          />
          <Route
            path="/hotel-report/thank-you"
            element={<ThankYou />}
          />
          {/* Legacy aliases — emails already sent still use /revenue-collection/form/... */}
          <Route
            path="/revenue-collection/form/:token"
            element={<LegacyHotelierFormRedirect />}
          />
          <Route
            path="/revenue-collection/thank-you"
            element={<LegacyThankYouRedirect />}
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </SentryRoutes>
      </Router>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;